import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IResidentTableRow } from '@/types/admin/residentList';
import { SVG_COMPONENTS } from '@/assets/admin';
import { capitalizeFirstLetter } from '@/utils/admin/utils';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { formatDateShort } from '@/utils/admin/date';
import { PersonCell } from '@/components/shared/redesign';
import { GlassStatusBadge, GlassStatusVariant } from '@/components/admin/redesign';

const statusToVariant = (s: string): GlassStatusVariant => {
  switch ((s || '').toLowerCase()) {
    case 'current':
      return 'success';
    case 'notice':
      return 'warning';
    case 'past':
    case 'cancelled':
      return 'error';
    case 'future':
      return 'info';
    default:
      return 'neutral';
  }
};

const useColumnStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      customerTypeText: {
        fontSize: 16,
        color: components.admin.dataCell.text,
      },
      autopayContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
      },
      autopayCenter: {
        alignSelf: 'center',
      },
      autopayText: {
        fontSize: 16,
        color: components.admin.dataCell.text,
      },
      infoText: {
        fontSize: 16,
        color: components.admin.dataCell.text,
      },
      lastPaymentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
      },
      lastPaymentAmount: {
        fontSize: 11,
        opacity: 0.7,
      },
      mobileText: {
        fontSize: 12,
        color: components.admin.dataCell.text,
        flexShrink: 1,
      },
      mobileBadgeAlignEnd: {
        alignSelf: 'flex-end',
      },
      autopayIconDim: {
        opacity: 0.7,
      },
    }),
  );

const InfoTextCell: React.FC<{ value: string | null | undefined; fallback?: string }> = ({
  value,
  fallback = 'N/A',
}) => {
  const styles = useColumnStyles();
  return <Text style={styles.infoText}>{value || fallback}</Text>;
};

const DateCell: React.FC<{ value: Date | null }> = ({ value }) => {
  const styles = useColumnStyles();
  return <Text style={styles.infoText}>{formatDateShort(value)}</Text>;
};

const LeaseStatusMobileBadge: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <GlassStatusBadge
      variant={statusToVariant(value)}
      label={capitalizeFirstLetter(value)}
      style={styles.mobileBadgeAlignEnd}
    />
  );
};

const LastPaymentCell: React.FC<{
  value: Date | null;
  amount: number | null | undefined;
}> = ({ value, amount }) => {
  const styles = useColumnStyles();
  return (
    <View>
      <Text style={styles.infoText}>{formatDateShort(value)}</Text>
      {amount != null ? (
        <Text style={[styles.infoText, styles.lastPaymentAmount]}>
          ${Number(amount).toFixed(2)}
        </Text>
      ) : null}
    </View>
  );
};

const CustomerTypeCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.customerTypeText} numberOfLines={1}>
      {capitalizeFirstLetter(value)}
    </Text>
  );
};

const CustomerTypeMobileCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.mobileText} numberOfLines={1}>
      {capitalizeFirstLetter(value)}
    </Text>
  );
};

const AutopayCell: React.FC<{ value: boolean }> = ({ value }) => {
  const styles = useColumnStyles();
  const { semantic } = useAdminColors();
  return (
    <View style={[styles.autopayContainer, styles.autopayCenter]}>
      {value ? (
        <SVG_COMPONENTS.CHECK_CIRCLE width={20} height={20} color={semantic.status.success} />
      ) : (
        <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={semantic.status.error} />
      )}
    </View>
  );
};

const AutopayMobileCell: React.FC<{ value: boolean }> = ({ value }) => {
  const styles = useColumnStyles();
  const { semantic } = useAdminColors();
  return (
    <View style={styles.autopayContainer}>
      <Text style={styles.mobileText} numberOfLines={1}>Autopay: </Text>
      {value ? (
        <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color={semantic.status.success} style={styles.autopayIconDim} />
      ) : (
        <SVG_COMPONENTS.X_CIRCLE width={12} height={12} color={semantic.status.error} style={styles.autopayIconDim} />
      )}
    </View>
  );
};

export function useResidentTableColumns(): IColumnConfig<IResidentTableRow>[] {
  return useMemo<IColumnConfig<IResidentTableRow>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        renderCell: (_value: string, row: IResidentTableRow) => {
          const fullName = row.name ?? '';
          const parts = fullName.split(' ');
          return (
            <PersonCell
              firstName={parts[0]}
              lastName={parts.slice(1).join(' ')}
              email={(row as any).email}
              size="sm"
            />
          );
        },
      },
      {
        key: 'lease_status',
        label: 'Lease Status',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'multiSelect',
        filterOptions: [
          { label: 'Current', value: 'current' },
          { label: 'Notice', value: 'notice' },
          { label: 'Future', value: 'future' },
          { label: 'Past', value: 'past' },
          { label: 'Cancelled', value: 'cancelled' },
        ],
        renderCell: (value: string) => (
          <GlassStatusBadge
            variant={statusToVariant(value)}
            label={capitalizeFirstLetter(value)}
          />
        ),
        renderMobileCell: (value: string) => (
          <LeaseStatusMobileBadge value={value} />
        ),
      },
      {
        key: 'unit',
        label: 'Unit',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: false,
        sortType: 'string',
        filterType: 'text',
        renderCell: (value: string) => <InfoTextCell value={value} />,
      },
      {
        key: 'last_login',
        label: 'Last Login',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: false,
        sortType: 'date',
        filterType: 'date',
        renderCell: (value: Date | null) => <DateCell value={value} />,
      },
      {
        key: 'last_payment',
        label: 'Last Resio Payment',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: false,
        sortType: 'date',
        filterType: 'date',
        renderCell: (value: Date | null, row: IResidentTableRow) => {
          const amount =
            (row as any).last_payment_amount ?? (row as any).lastPaymentAmount;
          return <LastPaymentCell value={value} amount={amount} />;
        },
      },
      {
        key: 'customer_type',
        label: 'Customer Type',
        sortable: true,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'select',
        filterOptions: [
          { label: 'Resident', value: 'resident' },
          { label: 'Guarantor', value: 'guarantor' },
          { label: 'Occupant', value: 'occupant' },
        ],
        renderCell: (value: string) => <CustomerTypeCell value={value} />,
        renderMobileCell: (value: string) => <CustomerTypeMobileCell value={value} />,
      },
      {
        key: 'autopay',
        label: 'Autopay',
        sortable: false,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'boolean',
        filterType: 'select',
        filterOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
        renderCell: (value: boolean) => <AutopayCell value={value} />,
        renderMobileCell: (value: boolean) => <AutopayMobileCell value={value} />,
      },
    ],
    [],
  );
}
